/**
 * `POST /v1/orgs` and `GET /v1/me/orgs` — the request shapes, and the slug rule both
 * ends of the wire need.
 *
 * The exemplar for R7: `orgs` is **born** on the shared-schema/op shape rather than
 * converted to it, so this file is what every later resource's schema module should look
 * like. Three things it establishes:
 *
 *   1. **The schema owns shape and nothing else.** Whether the caller is a member,
 *      whether a slug collides, whether a row is locked — those are facts about the
 *      database, and they live in the op. A schema that reached for them would be a
 *      validator that needs a connection.
 *   2. **Refusal copy is authored here, not inherited from zod.** `zod`'s defaults are
 *      nobody's copy (c29's rule, restated in R7's ruling): every message below is a
 *      sentence a person reads, and the migrating routes in c34 and c35 carry their
 *      existing `detail` strings across as custom messages rather than editing the tests
 *      that pin them.
 *   3. **`.strict()` is a security statement here, not tidiness.** See
 *      {@link createOrgSchema}.
 */
import { z } from "zod";
import { ROLES } from "../roles.js";
/**
 * The longest brand name this API accepts.
 *
 * **The API's own bound, not the database's** — `organizations.name` is bare `text` with
 * no CHECK, so nothing below this refuses a megabyte. Stated rather than left implicit
 * because the constraint test in `packages/db` covers patterns that *are* mirrored, and a
 * reader should not have to check whether this one is.
 */
export const MAX_ORG_NAME_LENGTH = 200;
/**
 * Create an organization.
 *
 * **`.strict()`, and this is the security boundary rather than a preference.** The
 * bootstrap route is the only write in the product that runs before any membership
 * exists, so its transaction is scoped by `withOrg` to an id the server just generated.
 * `withOrg` pointed at an attacker-chosen *existing* id would be a cross-tenant write
 * path whose only credential is a valid session. The server therefore generates the id
 * and this schema refuses to carry one at all — a body naming `id` is told so by name,
 * rather than having the field quietly dropped.
 *
 * Two mechanisms, probed apart (re-plan §2, probes 1 and 2): this refusal is the near,
 * readable one; row-level security refusing an insert that names any other organization's
 * id is the belt behind it, and it answers with zero rows rather than a sentence.
 *
 * `slug` is likewise absent: it is derived from the name by {@link orgSlug}, which both
 * the signup form's preview and the write path call, so the preview cannot disagree with
 * what is stored.
 */
export const createOrgSchema = z.strictObject({
    name: z
        .string("A brand name is required.")
        .trim()
        .min(1, "A brand name is required.")
        .max(MAX_ORG_NAME_LENGTH, `A brand name is at most ${MAX_ORG_NAME_LENGTH} characters.`),
}, {
    // The message a forged `id` actually produces, authored rather than inherited.
    // c33 shipped this refusal carrying zod's own "Unrecognized key(s) in object" —
    // which is nobody's copy, on the one refusal in this schema that is a security
    // boundary. A caller who sends an id should be told why it was refused, not shown
    // a validator's internal phrasing.
    error: "An organisation's id is generated by the server and may not be supplied. " +
        "Send the brand name alone.",
});
/** One row of `GET /v1/me/orgs`. The query that decides what the org header may be set to. */
export const membershipSummarySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    role: z.enum(ROLES),
});
/**
 * The URL-safe form of a brand name — **one function, two callers.**
 *
 * `organizations.slug` is `NOT NULL UNIQUE`, and the signup screen shows a slug preview
 * beside the name field (`screens.md` §1: *"Brand name only. Slug preview."*). A preview
 * computed in the SPA and a value computed in the API are two spellings of one rule, and
 * they would disagree on the first name carrying an apostrophe. So the rule lives in
 * `packages/shared`, which the browser bundle already imports.
 *
 * Deliberately ASCII-narrow: anything outside `[a-z0-9]` becomes a hyphen, runs collapse,
 * and the ends are trimmed. A name that reduces to nothing — *"株式会社"*, *"---"* —
 * yields the empty string, and the caller decides what to do with that rather than this
 * function inventing a name. The bootstrap op appends a disambiguator in that case, and
 * the open question of non-Latin slugs rides with the agency console (open-decisions #14),
 * where a second organization can be created deliberately.
 */
export function orgSlug(name) {
    return name
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, "-")
        .replaceAll(/^-+|-+$/g, "")
        .slice(0, 60);
}
//# sourceMappingURL=orgs.js.map